import { SetStateAction, useEffect, useRef, useState } from 'react';
import {
  getOnlyExistingKeys,
  withValues,
  getFilters,
  setFilters as setStorageFilters
} from 'utils';
import { useQueryParams } from './useQueryParams';
import { useQueryUpdate } from './useQueryUpdate';
import { $Object } from '../types';

export type onFilterChange<T> = (newValues: Partial<T>, reset?: boolean) => void;

export type FilterResult<T> = {
  filters: T;
  onFiltersChange: onFilterChange<T>;
};

type Options = {
  keyword: string;
  useQueryParams?: boolean;
  /**
   * Will get parsed from query with JSON.parse
   */
  objectKeys?: string[];
};

export const useFilters = <T extends $Object>(
  initialFilters: T,
  options: Options
): FilterResult<T> => {
  const storedFilters = getFilters();

  const localFilters = useRef<$Object>(storedFilters || {});

  const query = useQueryParams();
  const { updateQuery } = useQueryUpdate();

  const currentFilters = useRef<T>(
    getOnlyExistingKeys(
      initialFilters,
      Object.keys(query).length > 0 && !options.useQueryParams
        ? ({ ...initialFilters, ...query } as T)
        : localFilters.current[options.keyword]
          ? localFilters.current[options.keyword]
          : withValues(initialFilters)
    )
  );

  useEffect(() => {
    updateQuery(withValues(currentFilters.current));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);



  const [filters, setFilters] = useState(currentFilters.current);

  const onFiltersChange = (newValues: $Object, reset = false): void => {
    // Reset cursor when filters that affect results change
    const shouldResetCursor = 
      (newValues.search !== undefined && newValues.search !== filters.search) ||
      (newValues.ordering !== undefined && newValues.ordering !== filters.ordering) ||
      (newValues.documents_state !== undefined && newValues.documents_state !== filters.documents_state) ||
      (newValues.state !== undefined && newValues.state !== filters.state) ||
      (newValues.directory !== undefined && newValues.directory !== filters.directory) ||
      (newValues.created_at_after !== undefined && newValues.created_at_after !== filters.created_at_after) ||
      (newValues.created_at_before !== undefined && newValues.created_at_before !== filters.created_at_before) ||
      (newValues.per_page !== undefined && newValues.per_page !== filters.per_page);
    
    const baseFilters = {
      ...(!reset && query),
      ...newValues
    };
    
    // If cursor should be reset, remove it from filters
    if (shouldResetCursor && !newValues.cursor) {
      delete baseFilters.cursor;
      baseFilters.page = newValues.page || 1;
    }
    
    const newFilters = baseFilters;

    setFilters(newFilters as unknown as SetStateAction<T>);

    setStorageFilters({
      ...localFilters.current,
      [options.keyword]: newFilters
    });

    updateQuery(withValues(newFilters));
  };

  useEffect(() => {
    if (!options.useQueryParams) return;

    if (!Object.keys(query).length) {
      return onFiltersChange(withValues(filters));
    }
    
    onFiltersChange(withValues(query));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options.useQueryParams, JSON.stringify(query)]);

  return { filters, onFiltersChange };
};
